/**
 * Chat and Group Channels Contract — native real-time messaging between employees.
 *
 * Supports:
 * - 1:1 Direct Messaging between any two employees in the organisation.
 * - Group chats and Public/Private Channels with member invite & role management.
 * - Monotonic seq-ordered message history, unread counts, reactions, and colleague directory.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { errorBodySchema, uuidSchema } from '../schemas/common.js';
import { routeMeta } from './meta.js';

const c = initContract();

/**
 * The `topic` that marks a person's private "Approvals" conversation (one member, server-authored
 * cards only). One constant so the API, the gateway and the web app cannot disagree about which
 * conversation is the system one.
 */
export const APPROVALS_CONVERSATION_TOPIC = 'system:approvals';

/**
 * `searchMessages`'s pagination cursor — the `(ts_rank, seq)` composite of the last row of the
 * previous page. `rank` is a float, `seq` is a bigint carried as a string (JS numbers lose
 * precision above 2^53, and message `seq` is a Postgres `bigint`). Opaque to callers: encode with
 * `encodeChatSearchCursor`, never construct one by hand.
 *
 * Encoded as base64url JSON rather than `Buffer` — this package is RN-safe ESM with no Node
 * built-ins — using the universal `btoa`/`atob` globals (Node 18+, browsers, and React Native's
 * own polyfill). The payload is plain ASCII (digits, punctuation), so the classic
 * btoa/atob-is-not-unicode-safe trap doesn't apply here.
 */
export const chatSearchCursorDataSchema = z.object({
  rank: z.number(),
  seq: z.string().regex(/^\d+$/, 'seq must be a non-negative integer string'),
});
export type ChatSearchCursor = z.infer<typeof chatSearchCursorDataSchema>;

// `lib` is ES2023 (no DOM) so these Node 18+/browser/RN-polyfill globals have no ambient type here;
// declared locally rather than widening every consumer's tsconfig with the "DOM" lib.
declare function btoa(data: string): string;
declare function atob(data: string): string;

function toBase64Url(input: string): string {
  const base64 = btoa(input);
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function fromBase64Url(input: string): string {
  const base64 = input.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
  return atob(padded);
}

export function encodeChatSearchCursor(cursor: ChatSearchCursor): string {
  return toBase64Url(JSON.stringify(cursor));
}

/** The query-schema transform below is the only place a malformed cursor should surface as a
 * plain decode failure; this is exported too so the implementation and tests can decode/validate
 * without duplicating the try/catch. Returns `null` rather than throwing on anything malformed. */
export function decodeChatSearchCursor(raw: string): ChatSearchCursor | null {
  try {
    const parsed: unknown = JSON.parse(fromBase64Url(raw));
    const result = chatSearchCursorDataSchema.safeParse(parsed);
    return result.success ? result.data : null;
  } catch {
    return null;
  }
}

/** The query-param-facing schema: a base64url string on the wire, decoded to `ChatSearchCursor`
 * once parsed. A value that fails to decode is a 422, per CLAUDE.md's "validate at the edge" —
 * never an unhandled exception in the route handler. */
const chatSearchCursorSchema = z.string().transform((val, ctx) => {
  const decoded = decodeChatSearchCursor(val);
  if (decoded === null) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Invalid or corrupt search cursor' });
    return z.NEVER;
  }
  return decoded;
});

// ---- DLP review-queue cursor ------------------------------------------------------------
//
// The queue is ordered `createdAt DESC, id DESC`. A bare timestamp cursor skips rows that share a
// timestamp with the last row of a page, so the cursor carries both. `createdAt` is the row's
// full-precision UTC text (Postgres timestamps are microseconds, a JS Date is milliseconds), so it
// is a string, never re-parsed through `Date`.

const DLP_CURSOR_TS = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,6})?Z$/;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export interface ChatDlpCursor {
  /** Full-precision UTC timestamp text of the last row on the previous page. */
  createdAt: string;
  /** That row's id — `null` only for a legacy (timestamp-only) cursor from an older server. */
  id: string | null;
}

export function encodeChatDlpCursor(cursor: { createdAt: string; id: string }): string {
  return toBase64Url(`${cursor.createdAt}|${cursor.id}`);
}

/** `null` for anything that is not a cursor this contract produced. A plain ISO-8601 timestamp —
 * what the queue returned before the tuple cursor — is still accepted (as `id: null`) so a page
 * already open in someone's browser keeps paginating across the deploy. */
export function decodeChatDlpCursor(raw: string): ChatDlpCursor | null {
  if (DLP_CURSOR_TS.test(raw)) return { createdAt: raw, id: null };
  let text: string;
  try {
    text = fromBase64Url(raw);
  } catch {
    return null;
  }
  const parts = text.split('|');
  if (parts.length !== 2) return null;
  const [createdAt, id] = parts as [string, string];
  if (!DLP_CURSOR_TS.test(createdAt) || !UUID_RE.test(id)) return null;
  return { createdAt, id };
}

const chatDlpCursorSchema = z.string().max(200).transform((val, ctx) => {
  const decoded = decodeChatDlpCursor(val);
  if (decoded === null) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Invalid or corrupt cursor' });
    return z.NEVER;
  }
  return decoded;
});

/**
 * A boolean query flag. `z.coerce.boolean()` is `Boolean(value)`, so the string `"false"` — what
 * every HTTP client actually sends — became `true`. Only the literal words count; a boolean is
 * still accepted so typed callers that pass `true`/`false` keep compiling.
 */
export const queryBooleanSchema = z
  .union([z.boolean(), z.enum(['true', 'false'])])
  .transform((v) => v === true || v === 'true');

/** Cap on how many people one create/add request may name. */
export const CHAT_MAX_MEMBERS_PER_REQUEST = 500;

/**
 * Server-private keys inside a message's `meta` that must never reach an ordinary member's screen:
 * `editHistory` (the pre-edit bodies, kept for DLP/legal review). Every surface that sends `meta`
 * to a member — REST responses, live fanout, and the socket gateway's serialiser — passes it through
 * `chatPublicMeta` first.
 */
export const CHAT_PRIVATE_META_KEYS = ['editHistory'] as const;

export function chatPublicMeta(meta: unknown): Record<string, unknown> | null {
  if (!meta || typeof meta !== 'object' || Array.isArray(meta)) return null;
  const record = meta as Record<string, unknown>;
  if (!CHAT_PRIVATE_META_KEYS.some((k) => k in record)) return record;
  const copy: Record<string, unknown> = { ...record };
  for (const k of CHAT_PRIVATE_META_KEYS) delete copy[k];
  return copy;
}

export const chatEditHistoryEntrySchema = z.object({
  /** The body as it read before this edit (already DLP-redacted if it was flagged). */
  body: z.string().nullable(),
  /** When it was replaced. */
  replacedAt: z.string(),
});
export type ChatEditHistoryEntry = z.infer<typeof chatEditHistoryEntrySchema>;

export const chatMemberSchema = z.object({
  id: uuidSchema,
  personId: uuidSchema,
  fullName: z.string(),
  email: z.string().nullable().optional(),
  avatarUrl: z.string().nullable().optional(),
  role: z.enum(['owner', 'admin', 'member']),
  joinedAt: z.string(),
  /**
   * How far this member has read (a message `seq`). Drives the read ticks on the sender's own
   * messages. Only ever sent to people who are in the same conversation.
   */
  lastReadSeq: z.string().optional(),
});
export type ChatMember = z.infer<typeof chatMemberSchema>;

/** How long to mute a conversation. "off" un-mutes. */
export const chatMuteDurationSchema = z.enum(['1h', '8h', '24h', '7d', 'forever', 'off']);
export type ChatMuteDuration = z.infer<typeof chatMuteDurationSchema>;

export const chatArchivedChannelSchema = z.object({
  id: uuidSchema,
  name: z.string().nullable(),
  topic: z.string().nullable(),
  visibility: z.enum(['private', 'public']),
  archivedAt: z.string(),
  /** Whether the caller (an active owner/admin of it) may restore it. With the org setting
   * `archivedChannelsVisibleTo: 'everyone'` the list also carries public channels the caller can
   * only look at; restoring always stays with the channel's owners/admins. */
  canRestore: z.boolean().optional(),
});
export type ChatArchivedChannel = z.infer<typeof chatArchivedChannelSchema>;

export const chatConversationSnippetSchema = z.object({
  id: uuidSchema,
  type: z.enum(['dm', 'group', 'channel']),
  name: z.string().nullable().optional(),
  topic: z.string().nullable().optional(),
  visibility: z.enum(['private', 'public']),
  createdBy: uuidSchema.nullable().optional(),
  lastSeq: z.string(),
  lastMessageAt: z.string().nullable().optional(),
  unreadCount: z.number().int().nonnegative(),
  /** When this person's mute on the conversation ends (a far-future date means until turned off).
   * Null or absent: not muted. Personal — other members see nothing of it. */
  mutedUntil: z.string().nullable().optional(),
  members: z.array(chatMemberSchema),
  otherMember: chatMemberSchema.nullable().optional(),
  lastMessage: z
    .object({
      id: uuidSchema,
      seq: z.string(),
      senderId: uuidSchema.nullable().optional(),
      senderName: z.string().nullable().optional(),
      kind: z.string(),
      body: z.string().nullable().optional(),
      createdAt: z.string(),
    })
    .nullable()
    .optional(),
});
export type ChatConversationSnippet = z.infer<typeof chatConversationSnippetSchema>;

export const chatMessageReactionSchema = z.object({
  emoji: z.string(),
  count: z.number().int().positive(),
  reactedByMe: z.boolean(),
});
export type ChatMessageReaction = z.infer<typeof chatMessageReactionSchema>;

/** No storage key: bytes are only ever reached through `downloadChatAttachment`, which re-checks
 * conversation access on every fetch. */
export const chatAttachmentSchema = z.object({
  id: uuidSchema,
  fileName: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number().int().nonnegative(),
});
export type ChatAttachment = z.infer<typeof chatAttachmentSchema>;

/** Base64 JSON rather than multipart, for the reason `uploadExpenseAttachmentBodySchema` gives:
 * every route in this API is JSON end to end. The type and size are decided server-side. */
export const uploadChatAttachmentBodySchema = z.object({
  clientMsgId: z.string().uuid(),
  fileName: z.string().trim().min(1).max(255),
  /** What the browser claimed. Advisory only — the server sniffs the bytes and stores its own answer. */
  mimeType: z.string().trim().max(100).optional(),
  contentBase64: z.string().min(1),
  caption: z.string().trim().max(2000).optional(),
});

/**
 * The `meta` of a `kind: 'approval_card'` message.
 *
 * A SNAPSHOT for display, never the source of truth: `status` mirrors the underlying request and is
 * refreshed from it, and every Approve/Reject click is re-authorised against the real workflow on
 * the server. `fields` is a generic label/value list so a new request type adds a producer, not a
 * new card layout.
 */
export const approvalCardMetaSchema = z.object({
  entityType: z.enum(['leave_request']),
  entityId: uuidSchema,
  instanceId: uuidSchema,
  /** The workflow step this card was issued for — a card is bound to ONE approver's turn. */
  stepId: uuidSchema,
  status: z.enum(['pending', 'approved', 'rejected', 'withdrawn']),
  title: z.string(),
  requesterName: z.string(),
  fields: z.array(z.object({ label: z.string(), value: z.string() })),
  /** Set once decided, for the card's own footer. Absent while pending. */
  decidedByName: z.string().optional(),
});
export type ApprovalCardMeta = z.infer<typeof approvalCardMetaSchema>;

/** Max characters of the quoted parent's body carried in `replyTo.snippet`. */
export const CHAT_REPLY_SNIPPET_MAX = 140;

/**
 * WhatsApp-style reply quote: a compact view of the message a reply points at (`parentId`).
 * `snippet` is the first `CHAT_REPLY_SNIPPET_MAX` chars of the parent's STORED body (already
 * DLP-redacted), null when the parent was deleted for everyone (`deleted: true`) or has no body
 * (e.g. a file — the client labels it from `kind`).
 */
export const chatReplyToSchema = z.object({
  id: uuidSchema,
  senderId: uuidSchema.nullable(),
  senderName: z.string().nullable(),
  kind: z.string(),
  snippet: z.string().nullable(),
  deleted: z.boolean(),
});
export type ChatReplyTo = z.infer<typeof chatReplyToSchema>;

/** The parent-row fields `buildReplyTo` needs — satisfied by a Prisma `Message` row. */
export interface ChatReplyParentRow {
  id: string;
  senderId: string | null;
  kind: string;
  body: string | null;
  deletedAt: Date | string | null;
}

/**
 * Builds a reply's `replyTo` from its parent row. Pure — shared by the API and the ws gateway so
 * both put the identical quote on the wire. `senderName` is resolved by the caller (a name lookup
 * is I/O); a missing parent (null/undefined) yields null.
 */
export function buildReplyTo(
  parent: ChatReplyParentRow | null | undefined,
  senderName: string | null = null,
): ChatReplyTo | null {
  if (!parent) return null;
  const deleted = parent.deletedAt != null;
  const body = deleted ? null : parent.body;
  return {
    id: parent.id,
    senderId: parent.senderId,
    senderName,
    kind: parent.kind,
    snippet: body ? body.slice(0, CHAT_REPLY_SNIPPET_MAX) : null,
    deleted,
  };
}

export const chatMessageItemSchema = z.object({
  id: uuidSchema,
  conversationId: uuidSchema,
  seq: z.string(),
  senderId: uuidSchema.nullable().optional(),
  senderName: z.string().nullable().optional(),
  senderAvatar: z.string().nullable().optional(),
  kind: z.enum(['text', 'file', 'system', 'ai', 'approval_card']),
  body: z.string().nullable().optional(),
  parentId: uuidSchema.nullable().optional(),
  /** The quoted parent when this message is a reply (`parentId` set); null otherwise. */
  replyTo: chatReplyToSchema.nullable().optional(),
  meta: z.record(z.unknown()).nullable().optional(),
  reactions: z.array(chatMessageReactionSchema).optional(),
  attachments: z.array(chatAttachmentSchema).optional(),
  editedAt: z.string().nullable().optional(),
  deletedAt: z.string().nullable().optional(),
  createdAt: z.string(),
});
export type ChatMessageItem = z.infer<typeof chatMessageItemSchema>;

export const chatDirectoryColleagueSchema = z.object({
  personId: uuidSchema,
  fullName: z.string(),
  email: z.string().nullable().optional(),
  department: z.string().nullable().optional(),
  designation: z.string().nullable().optional(),
  avatarUrl: z.string().nullable().optional(),
});
export type ChatDirectoryColleague = z.infer<typeof chatDirectoryColleagueSchema>;

// ---- Bulk chat-history import (migrating off another platform) -------------------------
//
// Column mapping and date-format disambiguation happen client-side (same split as
// `importTasksBulk`'s `dueDate`) — the server only ever sees a fully-resolved row, never a raw
// spreadsheet cell, so it never has to guess a locale or a header name.

const importMessagesBulkRowSchema = z.object({
  /** The channel this row belongs to. Rows sharing the same (case-insensitive) name land in the
   * same conversation — created on first use, reused after. There is no DM/group distinction on
   * import: a two-person history becomes a private channel named after this column, same as a
   * larger one. */
  groupName: z.string().trim().min(1).max(160),
  senderEmail: z.string().trim().email(),
  /** Shown as the sender when `senderEmail` doesn't match any person currently in this org (e.g.
   * someone who has since exited) — the message still imports, just without a linked Person, so
   * it can never be @mentioned or attributed to a live account. Falls back to the email itself
   * when omitted. */
  senderName: z.string().trim().max(160).optional(),
  body: z.string().trim().min(1).max(16000),
  /** Full ISO 8601 with an explicit offset — the DD/MM vs. MM/DD ambiguity a source export's date
   * column carries is resolved client-side before this ever reaches the contract. */
  sentAt: z.string().datetime({ offset: true }),
});

export const importMessagesBulkBodySchema = z.object({
  rows: z.array(importMessagesBulkRowSchema).min(1).max(500),
});

export const importMessagesBulkResponseSchema = z.object({
  conversationsCreated: z.number().int(),
  messagesCreated: z.number().int(),
  /** Rows skipped because the same (channel, sender, sentAt, body) was already imported — re-running
   * the same file is a no-op rather than a second copy of the history. */
  duplicatesSkipped: z.number().int().optional(),
  /** One entry per row that failed — a bad row is skipped, never a reason to fail the whole
   * batch, same convention as `importTasksBulk`. */
  errors: z.array(z.object({ row: z.number().int(), reason: z.string() })),
});

/**
 * Per-organisation chat behaviour, stored in `organizations.settings.chat` (a JSON column — no
 * dedicated table). Read with `resolveChatSettings`, which tolerates a partial or malformed blob
 * and fills each missing/invalid field from `CHAT_SETTINGS_DEFAULTS`.
 */
export const chatSettingsSchema = z.object({
  /** Minutes after sending during which the author may edit a message. `null` = no limit. */
  editWindowMinutes: z.number().int().min(1).max(1440).nullable(),
  /** Minutes after sending during which the author may "delete for everyone". `null` = no limit.
   * "Delete for me" is never time-limited. */
  deleteForEveryoneWindowMinutes: z.number().int().min(1).max(43200).nullable(),
  /** Whether link previews are generated at all. */
  linkPreviews: z.boolean(),
  /** `proxy`: the server fetches the preview image and returns it inline as a `data:` URL (the
   * browser never contacts the third-party host). `off`: previews carry no image. */
  linkPreviewImages: z.enum(['off', 'proxy']),
  /** Who can see a person's online/away presence. */
  presence: z.enum(['everyone', 'shared_conversations', 'off']),
  /** Who may list archived channels. Restoring always stays with the channel's owner/admin. */
  archivedChannelsVisibleTo: z.enum(['admins', 'everyone']),
});
export type ChatSettings = z.infer<typeof chatSettingsSchema>;

export const CHAT_SETTINGS_DEFAULTS: ChatSettings = Object.freeze({
  editWindowMinutes: null,
  deleteForEveryoneWindowMinutes: null,
  linkPreviews: true,
  linkPreviewImages: 'proxy',
  presence: 'shared_conversations',
  archivedChannelsVisibleTo: 'admins',
}) as ChatSettings;

/**
 * Resolves the effective chat settings from an organisation's raw `settings` JSON. Pure and
 * total: anything that isn't a valid value for a field falls back to that field's default.
 * `linkPreviews` additionally honours the legacy top-level `settings.chatLinkPreviews` boolean when
 * `settings.chat.linkPreviews` is absent.
 */
export function resolveChatSettings(rawOrgSettings: unknown): ChatSettings {
  const root =
    rawOrgSettings && typeof rawOrgSettings === 'object' && !Array.isArray(rawOrgSettings)
      ? (rawOrgSettings as Record<string, unknown>)
      : {};
  const rawChat = root['chat'];
  const chat =
    rawChat && typeof rawChat === 'object' && !Array.isArray(rawChat)
      ? (rawChat as Record<string, unknown>)
      : {};
  const shape = chatSettingsSchema.shape;
  const pick = <K extends keyof ChatSettings>(key: K): ChatSettings[K] | undefined => {
    if (!(key in chat)) return undefined;
    const parsed = shape[key].safeParse(chat[key]);
    return parsed.success ? (parsed.data as ChatSettings[K]) : undefined;
  };

  let linkPreviews = pick('linkPreviews');
  if (linkPreviews === undefined && !('linkPreviews' in chat)) {
    const legacy = root['chatLinkPreviews'];
    if (typeof legacy === 'boolean') linkPreviews = legacy;
  }

  return {
    editWindowMinutes: pick('editWindowMinutes') ?? CHAT_SETTINGS_DEFAULTS.editWindowMinutes,
    deleteForEveryoneWindowMinutes:
      pick('deleteForEveryoneWindowMinutes') ??
      CHAT_SETTINGS_DEFAULTS.deleteForEveryoneWindowMinutes,
    linkPreviews: linkPreviews ?? CHAT_SETTINGS_DEFAULTS.linkPreviews,
    linkPreviewImages: pick('linkPreviewImages') ?? CHAT_SETTINGS_DEFAULTS.linkPreviewImages,
    presence: pick('presence') ?? CHAT_SETTINGS_DEFAULTS.presence,
    archivedChannelsVisibleTo:
      pick('archivedChannelsVisibleTo') ?? CHAT_SETTINGS_DEFAULTS.archivedChannelsVisibleTo,
  };
}

export const updateChatSettingsBodySchema = chatSettingsSchema
  .partial()
  .strict()
  .refine((body) => Object.keys(body).length > 0, {
    message: 'Provide at least one setting to change',
  });

export const chatSettingsResponseSchema = z.object({ settings: chatSettingsSchema });

export const getChatSettingsResponseSchema = z.object({
  settings: chatSettingsSchema,
  me: z.object({
    canViewArchived: z.boolean(),
    canManageSettings: z.boolean(),
  }),
});

export const chatContract = c.router(
  {
    listConversations: {
      method: 'GET',
      path: '/conversations',
      responses: {
        200: z.object({
          conversations: z.array(chatConversationSnippetSchema),
        }),
        401: errorBodySchema,
      },
      metadata: routeMeta(null),
      summary: 'List user active conversations (DMs, joined channels, and groups)',
    },

    getOrCreateDm: {
      method: 'POST',
      path: '/conversations/dm',
      body: z.object({
        recipientPersonId: uuidSchema,
      }),
      responses: {
        200: z.object({
          conversation: chatConversationSnippetSchema,
        }),
        201: z.object({
          conversation: chatConversationSnippetSchema,
        }),
        400: errorBodySchema,
        401: errorBodySchema,
        404: errorBodySchema,
      },
      metadata: routeMeta(null),
      summary: 'Get existing or create new 1:1 Direct Message with an employee',
    },

    createChannel: {
      method: 'POST',
      path: '/channels',
      body: z.object({
        name: z.string().min(1).max(100),
        topic: z.string().max(250).optional(),
        visibility: z.enum(['public', 'private']).default('private'),
        /** Every id must be a current colleague in the caller's organisation (422 otherwise). */
        memberPersonIds: z.array(uuidSchema).max(CHAT_MAX_MEMBERS_PER_REQUEST).optional(),
      }),
      responses: {
        201: z.object({
          conversation: chatConversationSnippetSchema,
        }),
        400: errorBodySchema,
        401: errorBodySchema,
        /** An unknown/inactive member id, or the reserved Approvals topic. */
        422: errorBodySchema,
      },
      metadata: routeMeta(null),
      summary: 'Create a new group or public/private channel',
    },

    listPublicChannels: {
      method: 'GET',
      path: '/channels/public',
      responses: {
        200: z.object({
          channels: z.array(chatConversationSnippetSchema),
        }),
        401: errorBodySchema,
      },
      metadata: routeMeta(null),
      summary: 'List all discoverable public channels in the organization',
    },

    joinChannel: {
      method: 'POST',
      path: '/channels/:id/join',
      pathParams: z.object({
        id: uuidSchema,
      }),
      body: z.object({}),
      responses: {
        200: z.object({
          conversation: chatConversationSnippetSchema,
        }),
        400: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      metadata: routeMeta(null),
      summary: 'Join a public channel',
    },

    leaveChannel: {
      method: 'POST',
      path: '/channels/:id/leave',
      pathParams: z.object({
        id: uuidSchema,
      }),
      body: z.object({}),
      responses: {
        200: z.object({
          success: z.literal(true),
        }),
        400: errorBodySchema,
        404: errorBodySchema,
        /** The caller is the last owner/admin and other members remain — promote someone first. */
        409: errorBodySchema,
      },
      metadata: routeMeta(null),
      summary: 'Leave a group or channel',
    },

    addMembers: {
      method: 'POST',
      path: '/channels/:id/members',
      pathParams: z.object({
        id: uuidSchema,
      }),
      body: z.object({
        /** Every id must be a current colleague in the caller's organisation (422 otherwise). */
        personIds: z.array(uuidSchema).min(1).max(CHAT_MAX_MEMBERS_PER_REQUEST),
      }),
      responses: {
        200: z.object({
          addedMembers: z.array(chatMemberSchema),
        }),
        400: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        422: errorBodySchema,
      },
      metadata: routeMeta(null),
      summary: 'Add one or more members to a group/channel',
    },

    removeMember: {
      method: 'DELETE',
      path: '/channels/:id/members/:personId',
      pathParams: z.object({
        id: uuidSchema,
        personId: uuidSchema,
      }),
      body: z.object({}),
      responses: {
        200: z.object({
          success: z.literal(true),
        }),
        400: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** Removing would leave the group with members but no owner/admin. */
        409: errorBodySchema,
      },
      metadata: routeMeta(null),
      summary: 'Remove a member from a group/channel',
    },

    updateMemberRole: {
      method: 'PATCH',
      path: '/channels/:id/members/:personId',
      pathParams: z.object({
        id: uuidSchema,
        personId: uuidSchema,
      }),
      body: z.object({
        role: z.enum(['admin', 'member']),
      }),
      responses: {
        200: z.object({
          member: chatMemberSchema,
        }),
        400: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      metadata: routeMeta(null),
      summary: 'Update member role (promote to admin or demote to member)',
    },

    updateChannel: {
      method: 'PATCH',
      path: '/channels/:id',
      pathParams: z.object({ id: uuidSchema }),
      body: z
        .object({
          name: z.string().trim().min(1).max(100).optional(),
          topic: z.string().trim().max(250).nullable().optional(),
        })
        .refine((v) => v.name !== undefined || v.topic !== undefined, {
          message: 'Provide a name or a topic to change',
        }),
      responses: {
        200: z.object({
          conversation: z.object({
            id: uuidSchema,
            name: z.string().nullable(),
            topic: z.string().nullable(),
          }),
        }),
        400: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      metadata: routeMeta(null),
      summary: 'Rename a channel or change its topic (channel owners and admins only)',
    },

    archiveChannel: {
      method: 'POST',
      path: '/channels/:id/archive',
      pathParams: z.object({ id: uuidSchema }),
      body: z.object({}),
      responses: {
        200: z.object({ success: z.literal(true) }),
        403: errorBodySchema,
        404: errorBodySchema,
      },
      metadata: routeMeta(null),
      summary:
        'Archive a channel: it leaves everyone’s list and can no longer be posted to (channel owners and admins only)',
    },

    unarchiveChannel: {
      method: 'POST',
      path: '/channels/:id/unarchive',
      pathParams: z.object({ id: uuidSchema }),
      body: z.object({}),
      responses: {
        200: z.object({ success: z.literal(true) }),
        403: errorBodySchema,
        404: errorBodySchema,
      },
      metadata: routeMeta(null),
      summary: 'Restore an archived channel (channel owners and admins only)',
    },

    listArchivedChannels: {
      method: 'GET',
      path: '/channels/archived',
      responses: {
        200: z.object({ channels: z.array(chatArchivedChannelSchema) }),
        401: errorBodySchema,
      },
      metadata: routeMeta(null),
      summary:
        'Archived channels the caller owns or administers (plus, when the org allows everyone to see them, archived public channels)',
    },

    setConversationMute: {
      method: 'PUT',
      path: '/conversations/:id/mute',
      pathParams: z.object({ id: uuidSchema }),
      body: z.object({ duration: chatMuteDurationSchema }),
      responses: {
        200: z.object({ mutedUntil: z.string().nullable() }),
        403: errorBodySchema,
        404: errorBodySchema,
      },
      metadata: routeMeta(null),
      summary: 'Mute or un-mute push notifications for one conversation (personal, per member)',
    },

    /**
     * Advance the caller's read pointer. The REST twin of the `message:read` socket event, so read
     * state (and unread badges) does not silently depend on the websocket being connected.
     * Monotonic: a lower `seq` than the stored one is a no-op; a `seq` past the conversation's last
     * message is clamped to it.
     */
    markConversationRead: {
      method: 'PUT',
      path: '/conversations/:id/read',
      pathParams: z.object({ id: uuidSchema }),
      body: z.object({
        /** A message `seq` (a Postgres bigint, carried as a digit string). */
        seq: z.string().regex(/^\d{1,19}$/, 'seq must be a non-negative integer string'),
      }),
      responses: {
        200: z.object({ lastReadSeq: z.string() }),
        404: errorBodySchema,
        422: errorBodySchema,
      },
      metadata: routeMeta(null),
      summary: 'Mark a conversation read up to a message seq (personal, per member)',
    },

    getMessages: {
      method: 'GET',
      path: '/conversations/:id/messages',
      pathParams: z.object({
        id: uuidSchema,
      }),
      query: z.object({
        limit: z.coerce.number().int().min(1).max(100).default(50),
        /** A message `seq` (a Postgres bigint, carried as a digit string). */
        beforeSeq: z
          .string()
          .regex(/^\d{1,19}$/, 'beforeSeq must be a non-negative integer string')
          .optional(),
      }),
      responses: {
        200: z.object({
          messages: z.array(chatMessageItemSchema),
          hasMore: z.boolean(),
        }),
        403: errorBodySchema,
        404: errorBodySchema,
        422: errorBodySchema,
      },
      metadata: routeMeta(null),
      summary: 'Fetch sequential message history for a conversation',
    },

    createMessage: {
      method: 'POST',
      path: '/conversations/:id/messages',
      pathParams: z.object({
        id: uuidSchema,
      }),
      body: z.object({
        clientMsgId: z.string().uuid(),
        body: z.string().max(16000).optional(),
        kind: z.enum(['text', 'file']).default('text'),
        parentId: uuidSchema.optional(),
        meta: z.record(z.unknown()).optional(),
        /** Person ids the composer's @-autocomplete resolved from the typed text. The server
         * re-validates every id against live conversation membership before treating it as a real
         * mention or notifying anyone — never trusted as-is, same "server is the boundary" rule
         * this route already applies to `meta` (client-supplied `meta` is dropped; only the
         * server's own DLP finding is written). */
        mentions: z.array(uuidSchema).max(50).optional(),
      }),
      responses: {
        201: z.object({
          message: chatMessageItemSchema,
        }),
        400: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** `clientMsgId` already belongs to someone else's message in this conversation. */
        409: errorBodySchema,
      },
      metadata: routeMeta(null),
      summary: 'Post a new message in a conversation (REST fallback or direct)',
    },

    /** Stores the file and creates the `file` message that carries it in one step — the attachments
     * table requires a parent message, so there is no "upload now, attach later". Access is a
     * conversation-membership check in the handler, hence `routeMeta(null)` like every chat route.
     *
     * `ai: 'hidden'`: the body is the file itself (`contentBase64`). A derived copilot tool would ask
     * the model to emit a whole file as base64, which it cannot do (see
     * packages/ai/src/tools/contract-tools.file-upload.test.ts). */
    uploadChatAttachment: {
      method: 'POST',
      path: '/conversations/:id/attachments',
      pathParams: z.object({ id: uuidSchema }),
      body: uploadChatAttachmentBodySchema,
      responses: {
        201: z.object({ message: chatMessageItemSchema }),
        403: errorBodySchema,
        404: errorBodySchema,
        /** `clientMsgId` already belongs to a different message in this conversation. */
        409: errorBodySchema,
        /** Rejected type, empty/invalid base64, over the size limit, or blocked by DLP. */
        422: errorBodySchema,
      },
      metadata: routeMeta(null, { ai: 'hidden' }),
      summary: 'Upload a file into a conversation as a message',
    },

    /**
     * Approve or reject the request an approval card stands for. The card is only a handle: the
     * handler re-runs the request's own decision path (assignee-or-override authorisation, pending
     * check, self-approval bar) and reflects the real outcome back onto the card.
     */
    decideApprovalCard: {
      method: 'POST',
      path: '/approval-cards/:messageId/decide',
      pathParams: z.object({ messageId: uuidSchema }),
      body: z.object({
        decision: z.enum(['approved', 'rejected']),
        comment: z.string().trim().max(1000).optional(),
      }),
      responses: {
        200: z.object({ message: chatMessageItemSchema }),
        403: errorBodySchema,
        404: errorBodySchema,
        /** Already decided, withdrawn, or no longer this approver's turn. */
        412: errorBodySchema,
      },
      metadata: routeMeta(null),
      summary: 'Decide the request behind an approval card',
    },

    /** `ai: 'hidden'` for the same reason as the upload: the response is the whole file as base64,
     * which would be pulled verbatim into the model's context (the payslip download precedent). */
    downloadChatAttachment: {
      method: 'GET',
      path: '/attachments/:attachmentId',
      pathParams: z.object({ attachmentId: uuidSchema }),
      responses: {
        200: z.object({
          fileName: z.string(),
          mimeType: z.string(),
          contentBase64: z.string(),
        }),
        401: errorBodySchema,
        404: errorBodySchema,
      },
      metadata: routeMeta(null, { ai: 'hidden' }),
      summary: 'Download one chat attachment (conversation access re-checked)',
    },

    getDirectory: {
      method: 'GET',
      path: '/directory',
      query: z.object({
        search: z.string().optional(),
      }),
      responses: {
        200: z.object({
          colleagues: z.array(chatDirectoryColleagueSchema),
        }),
        401: errorBodySchema,
      },
      metadata: routeMeta(null),
      summary: 'Search active colleagues in organization for chat and channel invites',
    },

    /**
     * REST twin of the socket's `message:edit` — same author-only gate, same DLP re-screen, same
     * "text only, not yet deleted" preconditions (`apps/ws/src/messages.ts`'s `editMessage`). Exists
     * for the same reason `createMessage` does: a backgrounded mobile app or a reconnect window
     * should not make editing your own message impossible until the socket comes back.
     *
     * `id` is the message id alone, not `convId` + `messageId` like the socket schema — the
     * conversation is derived from the message row, matching `forwardMessage`'s "identify by
     * message id" shape for this file's other by-message routes.
     */
    editMessage: {
      method: 'PUT',
      path: '/messages/:id',
      pathParams: z.object({ id: uuidSchema }),
      body: z.object({
        body: z.string().trim().min(1).max(16000),
      }),
      responses: {
        200: z.object({ message: chatMessageItemSchema }),
        /** Not a member of the message's conversation, or not the sender. */
        403: errorBodySchema,
        /** "Don't leak existence": a message that doesn't exist and one in a conversation the
         * caller cannot see are indistinguishable to them. */
        404: errorBodySchema,
        /** Blocked by DLP, already deleted, or not a text message. */
        412: errorBodySchema,
      },
      metadata: routeMeta(null),
      summary:
        "Edit the caller's own text message (REST fallback for the socket's message:edit)",
    },

    /**
     * REST twin of the socket's `message:delete` — a soft delete (tombstone), never a row removal,
     * exactly as `apps/ws/src/messages.ts`'s `deleteMessage` performs. Idempotent: deleting an
     * already-deleted message returns its current (already-tombstoned) state rather than erroring.
     */
    deleteMessage: {
      method: 'DELETE',
      path: '/messages/:id',
      pathParams: z.object({ id: uuidSchema }),
      body: z.object({}),
      responses: {
        200: z.object({ message: chatMessageItemSchema }),
        /** Not a member of the message's conversation, or not the sender. */
        403: errorBodySchema,
        /** Same "don't leak existence" convention as `editMessage` above. */
        404: errorBodySchema,
        /** The conversation is archived or under legal hold, or the org's
         * `deleteForEveryoneWindowMinutes` has passed since the message was sent. */
        412: errorBodySchema,
      },
      metadata: routeMeta(null),
      summary:
        "Delete the caller's own message for everyone — a tombstone (REST fallback for the socket's message:delete)",
    },

    /**
     * "Delete for me": hides one message from the CALLER's own views only (history, search, the
     * conversation list preview). Any active member may hide any message of the conversation. The
     * message row is never touched — other members still see it, and legal hold / retention / DLP
     * / eDiscovery are unaffected. Idempotent: hiding an already-hidden message is a 200.
     */
    deleteMessageForMe: {
      method: 'DELETE',
      path: '/messages/:id/me',
      pathParams: z.object({ id: uuidSchema }),
      body: z.object({}),
      responses: {
        200: z.object({ messageId: uuidSchema }),
        /** Not found, or in a conversation the caller is not an active member of. */
        404: errorBodySchema,
        /** Malformed message id. */
        422: errorBodySchema,
      },
      metadata: routeMeta(null),
      summary: 'Delete a message for me only (hide it from my own views)',
    },

    forwardMessage: {
      method: 'POST',
      path: '/messages/:id/forward',
      pathParams: z.object({ id: uuidSchema }),
      body: z.object({
        toConversationId: uuidSchema,
        /** An optional note added alongside the forwarded text — never edits the original. */
        comment: z.string().trim().max(2000).optional(),
      }),
      responses: {
        201: z.object({ message: chatMessageItemSchema }),
        403: errorBodySchema,
        404: errorBodySchema,
        /** Non-text source message, deleted source message, blocked by DLP, or archived target. */
        422: errorBodySchema,
      },
      metadata: routeMeta(null),
      summary:
        'Forward a text message into another conversation the caller is a member of — v1 supports text only, not files or approval cards',
    },

    /** Full-text search across every conversation the caller is a member of (or one, with
     * `conversationId`) — Postgres `tsvector`/`websearch_to_tsquery`, ranked by relevance then
     * recency. A soft-deleted message's tombstone never matches: its body is gone.
     *
     * Results are ordered by a COMPOSITE key (`ts_rank(...) DESC, seq DESC`), so — unlike a plain
     * single-column `createdAt` cursor — the cursor here has to carry both values from the last
     * row of the previous page. It's an opaque base64url string encoding
     * `{ rank, seq }`; the row-wise comparison `(rank, seq) < (cursor.rank, cursor.seq)` in the
     * implementation continues the same ordering without OFFSET's "the result set shifted under
     * you" bug (a message inserted between page fetches can change which rows rank where; OFFSET
     * skips or repeats rows around that shift, a tuple cursor never does). */
    searchMessages: {
      method: 'GET',
      path: '/messages/search',
      query: z.object({
        q: z.string().trim().min(1).max(200),
        conversationId: uuidSchema.optional(),
        limit: z.coerce.number().int().min(1).max(50).default(20),
        /** Opaque — echoed back verbatim from the previous page's `nextCursor`. Never construct
         * one by hand; a value that doesn't decode to `{ rank, seq }` fails validation (422). */
        cursor: chatSearchCursorSchema.optional(),
      }),
      responses: {
        200: z.object({
          items: z.array(
            z.object({
              message: chatMessageItemSchema,
              conversationName: z.string().nullable(),
              conversationType: z.enum(['dm', 'group', 'channel']),
            }),
          ),
          nextCursor: z.string().nullable(),
        }),
        422: errorBodySchema,
      },
      metadata: routeMeta(null),
      summary: 'Search message bodies across the caller\'s own conversations',
    },

    /**
     * CHT-14: the DLP-flag review queue. `chat.message.dlp_flagged` already notifies HR/admin the
     * moment a message is redacted, and every flagged message already records
     * `meta.dlp = {rules, flaggedAt, reviewed}` — "so a review queue is a query rather than a log
     * search" (`apps/ws/src/messages.ts`'s own comment on that field). This is that query: only the
     * rule names and the message's own already-redacted body are ever returned, never the original
     * matched text.
     */
    listDlpFlaggedMessages: {
      method: 'GET',
      path: '/dlp-flagged',
      query: z.object({
        limit: z.coerce.number().int().min(1).max(100).default(50),
        /** Opaque — echoed back verbatim from the previous page's `nextCursor` (a `(createdAt, id)`
         * tuple). A value that doesn't decode fails validation (422). */
        cursor: chatDlpCursorSchema.optional(),
        /** Defaults to unreviewed-only — the queue an admin actually works from. Pass `true` to
         * see the already-cleared history instead. Only `true`/`"true"` mean true. */
        reviewed: queryBooleanSchema.default(false),
      }),
      responses: {
        200: z.object({
          items: z.array(
            z.object({
              /** A flagged message the author later deleted stays in the queue: `deletedAt` is set
               * and the (already-redacted) body is kept for the reviewer. */
              message: chatMessageItemSchema,
              conversationName: z.string().nullable(),
              conversationType: z.enum(['dm', 'group', 'channel']),
              rules: z.array(z.string()),
              flaggedAt: z.string(),
              reviewed: z.boolean(),
              reviewedBy: uuidSchema.nullable().optional(),
              reviewedAt: z.string().nullable().optional(),
              /** Earlier bodies of an edited message, oldest first. Reviewer-only — never part of
               * an ordinary message response. */
              editHistory: z.array(chatEditHistoryEntrySchema).optional(),
            }),
          ),
          nextCursor: z.string().nullable(),
        }),
        401: errorBodySchema,
        403: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'List messages the DLP scanner flagged, defaulting to unreviewed ones',
      metadata: routeMeta('chat.dlp:review'),
    },

    markDlpMessageReviewed: {
      method: 'POST',
      path: '/dlp-flagged/:id/review',
      pathParams: z.object({ id: uuidSchema }),
      body: z.object({}),
      responses: {
        200: z.object({ reviewed: z.literal(true) }),
        401: errorBodySchema,
        403: errorBodySchema,
        /** No such message, or a message that never carried a DLP flag. */
        404: errorBodySchema,
      },
      summary: 'Mark one DLP-flagged message as reviewed',
      metadata: routeMeta('chat.dlp:review'),
    },

    importMessagesBulk: {
      method: 'POST',
      path: '/import',
      body: importMessagesBulkBodySchema,
      responses: {
        201: importMessagesBulkResponseSchema,
        401: errorBodySchema,
        403: errorBodySchema,
      },
      // ai: 'hidden' — same reasoning as `importTasksBulk`: up to 500 pre-parsed rows is not
      // something a chat model authors, and historical chat content from another platform is
      // exactly the kind of bulk write that stays behind the dedicated import screen, never a
      // copilot-derived tool.
      metadata: routeMeta('chat.import:execute', { ai: 'hidden' }),
      summary:
        'Bulk-import historical chat messages from another platform (e.g. a migration) — ' +
        'groups rows into channels by groupName, matches senders by email, skips and reports bad rows individually',
    },

    /** SSRF-guarded and cached server-side — see `apps/api/src/lib/link-preview.ts`. Any signed-in
     * user may preview any URL (there is no conversation/tenant data involved), rate-limited per
     * person to keep this endpoint from being used as a general-purpose URL fetcher. */
    getLinkPreview: {
      method: 'GET',
      path: '/link-preview',
      query: z.object({ url: z.string().url() }),
      responses: {
        200: z.object({
          preview: z
            .object({
              url: z.string(),
              title: z.string().nullable(),
              description: z.string().nullable(),
              /** When the org proxies preview images this is an inline `data:image/...;base64,`
               * URL fetched server-side; the third-party image URL is never returned. `null` when
               * the org has preview images off or the page had no usable image. */
              imageUrl: z.string().nullable(),
              siteName: z.string().nullable(),
            })
            .nullable(),
        }),
        /** A malformed `url` query param fails Zod validation. */
        422: errorBodySchema,
        429: errorBodySchema,
      },
      metadata: routeMeta(null),
      summary: 'Fetch Open Graph preview metadata for a URL, for chat link previews',
    },

    /** The organisation's effective chat settings (defaults filled in) plus what the caller may do
     * with them. Any signed-in member may read them — the web and mobile clients need them to
     * render (edit window, presence, archived-channels entry point). */
    getChatSettings: {
      method: 'GET',
      path: '/settings',
      responses: {
        200: getChatSettingsResponseSchema,
        401: errorBodySchema,
      },
      metadata: routeMeta(null),
      summary: "Get the organisation's chat settings and the caller's chat capabilities",
    },

    /** Partial update — only the keys sent are changed; every other organisation setting (DLP
     * rules, legacy keys, …) is preserved. Audited as `chat.settings_changed`. */
    updateChatSettings: {
      method: 'PUT',
      path: '/settings',
      body: updateChatSettingsBodySchema,
      responses: {
        200: chatSettingsResponseSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        422: errorBodySchema,
      },
      metadata: routeMeta('chat.settings:manage'),
      summary: "Update the organisation's chat settings (edit window, link previews, presence, archive visibility)",
    },
  },
  {
    pathPrefix: '/chat',
  },
);
